import React, { useEffect, useState } from 'react';
import { GlobalizationProvider, setMarketCookies } from '@rcg/ui-react';
import { ThemeProvider } from '@material-ui/core';
import fetchGlobalization from '../../operations/dataAccess/fetchGlobalization';
import { marketPathVar } from '../../operations/localState/reactiveVariables';
import {
  mapLocaleToGlobalPayload,
  resolveDictionaryLang,
} from '../../utils/globalization/mapLocale';
import {
  cookieCountry,
  cookieLanguage,
  siteCountryCookie,
} from '../../utils/globalization/marketCookies';
import AirLoader from '../../components/AirLoader';
import Theme from '../../utils/theme';
import CelebrityTheme from '../../utils/theme/celebrity';
import getBrand from '../../utils/getBrand';
import { BRAND } from '../../utils/constants';

// Build-time, not LaunchDarkly: useLdFlags reads useGlobal(), so gating globalization on an LD
// flag would need globalization to resolve before the flag that decides how to resolve it.
const enableG11n = () => process.env.REACT_APP_ENABLE_G11N_GLOBALIZATION === 'true';

// One budget for the whole startup, shared by every call, rather than a timeout each. Startup is up
// to four sequential round trips (two locale lookups plus the two AEM reads) and nothing renders
// until they settle, so per-call timeouts would add up instead of bounding anything. Whatever has
// arrived when this expires is what gets used: an aborted dictionary still degrades to the bundled
// one, while an aborted locale is fatal and surfaces as the error state.
const STARTUP_BUDGET_MS = 10_000;

// Uncached on purpose: the provider mounts once per page load, and these two go from the browser
// straight to AEM, so the BFF's cache does not cover them.
async function fetchJson(url, signal) {
  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error(`${response.status} fetching ${url}`);
  return response.json();
}

// Two or three letters: Royal's market slugs are three (/usa/fr, /lac/es), Celebrity's are two
// (/ca/fr, /gb). ui-react's own regex takes only three, so it cannot read a Celebrity path.
const PATH_LOCALE = /^\/(?<market>[a-zA-Z]{2,3})(?:\/(?<language>[a-zA-Z]{2}))?(?:\/|$)/;

const MARKET_NOT_FOUND = 'MARKET_NOT_FOUND';
const LANGUAGE_NOT_AVAILABLE_FOR_COUNTRY = 'LANGUAGE_NOT_AVAILABLE_FOR_COUNTRY';

export const parsePathLocale = (pathname = '') => {
  const groups = pathname.match(PATH_LOCALE)?.groups;
  if (!groups) return null;
  return {
    market: groups.market.toLowerCase(),
    language: groups.language?.toLowerCase(),
  };
};

/**
 * The URL's locale segment is a MARKET, not a country, and must be queried as one: passing it as
 * `country` resolves the wrong country wherever the two differ — every shared market (LAC groups 16
 * countries, usa groups USA/CAN/PRI) and every Celebrity market. It is also what makes Canadian
 * French work, since fr belongs to CAN at the country level and not to the usa market.
 */
// The forcing params have done their job once the locale is resolved and the cookies are written,
// and the market they select is already reflected in the path — except where a market groups
// several countries under one path (Royal's `usa` covers USA/CAN/PRI), and there the cookie carries
// it. Leaving them in the address bar makes every later share re-force a country the recipient
// never asked for.
function stripForcingParams() {
  const params = new URLSearchParams(window.location.search);
  if (!params.has('country') && !params.has('wuc')) return;
  params.delete('country');
  params.delete('wuc');
  // Order is preserved by URLSearchParams, which matters: restClient reads a deep link's token
  // with substring(7), so `?token=` has to stay first when it was.
  const query = params.toString();
  // replaceState, not navigate: this must not add a history entry, and reach-router would remount.
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
  );
}

async function resolveLocale(signal) {
  const pathLocale = parsePathLocale(window.location.pathname);

  // ?country= (or ?wuc=) forces a market from a link — QA and marketing deep links. A booking deep
  // link is different: manage appends its own country next to the token, and for a guest who
  // already carries `wuc` that country must not overrule the market they picked. Only the country
  // is forced, so the language still comes from the path when the link carries one.
  const forced = new URLSearchParams(window.location.search);
  const forcedCountry = forced.get('country') || forced.get('wuc');
  const appendedToBookingLink = forced.has('token') && Boolean(siteCountryCookie());
  if (forcedCountry && !appendedToBookingLink) {
    return fetchGlobalization(
      {
        country: forcedCountry.toLowerCase(),
        language: pathLocale?.language || cookieLanguage(),
      },
      signal,
    );
  }

  if (pathLocale) {
    try {
      const locale = await fetchGlobalization(pathLocale, signal);
      // A slug that is not one of the brand's markets gets the brand's DEFAULT market on a 200,
      // flagged in meta.fallback. Accepting it would serve that market's currency and office.
      if (locale?.meta?.fallback?.type !== MARKET_NOT_FOUND) return locale;
    } catch (error) {
      // Degrade to the cookie rather than leaving the app with no locale.
      // eslint-disable-next-line no-console
      console.warn(error);
    }
  }

  const language = cookieLanguage();
  const cookieLocale = await fetchGlobalization({ country: cookieCountry(), language }, signal);
  const fallback = cookieLocale?.meta?.fallback;
  if (fallback?.type !== LANGUAGE_NOT_AVAILABLE_FOR_COUNTRY || !fallback.applied?.market) {
    return cookieLocale;
  }
  try {
    const marketLocale = await fetchGlobalization(
      { market: fallback.applied.market, language },
      signal,
    );
    return marketLocale?.meta?.resolved ? marketLocale : cookieLocale;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(error);
    return cookieLocale;
  }
}

// cacheTime applies to the killswitch branch only — ui-react caches its own AEM fetches with it.
const AirGlobalizationProvider = ({ aemUrl, cacheTime, dicName, children }) => {
  const [state, setState] = useState({ loading: true, error: null, payload: null, dictionary: {} });

  useEffect(() => {
    let cancelled = false;
    const budget = new AbortController();
    const budgetTimer = setTimeout(() => budget.abort(), STARTUP_BUDGET_MS);

    if (enableG11n()) {
      (async () => {
        try {
          // Both soft-fail: a missing dictionary must degrade to the bundled defaults, not block
          // the app.
          const i18nPromise = fetchJson(`${aemUrl}i18nConversion`, budget.signal).catch((error) => {
            // eslint-disable-next-line no-console
            console.warn(error);
            return {};
          });

          const locale = await resolveLocale(budget.signal);
          const i18nConversion = await i18nPromise;

          const payload = mapLocaleToGlobalPayload(locale, i18nConversion);
          let dictionary = {};
          try {
            const langCd = resolveDictionaryLang(locale, i18nConversion);
            dictionary = await fetchJson(
              `${aemUrl}dictionary?dictName=${encodeURIComponent(
                dicName,
              )}&langCd=${encodeURIComponent(langCd)}`,
              budget.signal,
            );
          } catch (error) {
            // eslint-disable-next-line no-console
            console.warn(error);
          }

          if (cancelled) return;

          // cookieCountry()/cookieLanguage() read these cookies, and they set the country/language
          // headers on every Apollo and BFF request — skipping it sends the default market.
          // On a Celebrity host ui-react writes only `wuc`/`country`, and host-only rather than on
          // the registrable domain, so the language it is given here never reaches a cookie.
          setMarketCookies({ country: payload.code, language: payload.language });

          // Only once the cookies hold the answer: strip earlier and a reload before this point
          // would fall back to the default market instead of the one that was asked for.
          stripForcingParams();

          // Before children mount, like the cookies: every path helper reads it.
          if (payload.marketPath) {
            marketPathVar(payload.marketPath);
            if (!payload.marketPath.startsWith('/')) {
              // Celebrity's `int` market answers with the local franchise's own domain for FRA,
              // DEU, ITA and ESP. globalizationPath ignores it rather than send the guest off
              // site, so the app runs unprefixed here — say so, because it is a product question.
              // eslint-disable-next-line no-console
              console.warn(
                `globalization returned an off-site marketPath (${payload.marketPath}); running unprefixed`,
              );
            }
          } else {
            // Without it the path helpers fall back to composing `/{market}/{lang}`, which is wrong
            // for Celebrity — /us/en instead of the root, and a language segment on /gb and /ie.
            // eslint-disable-next-line no-console
            console.warn('globalization returned no marketPath; URL prefixes will be composed');
          }

          setState({ loading: false, error: null, payload, dictionary });
        } catch (error) {
          if (!cancelled) setState({ loading: false, error, payload: null, dictionary: {} });
        } finally {
          // Startup is over either way — leaving the timer armed would abort a controller nothing
          // is listening to, and keep a pending timer for the rest of the budget.
          clearTimeout(budgetTimer);
        }
      })();
    }

    return () => {
      cancelled = true;
      clearTimeout(budgetTimer);
      // Cancels whatever is still in flight instead of letting it finish into a dead component.
      budget.abort();
    };
  }, [aemUrl, dicName]);

  if (!enableG11n()) {
    return (
      <GlobalizationProvider url={aemUrl} cacheTime={cacheTime} dicName={dicName}>
        {children}
      </GlobalizationProvider>
    );
  }

  // GlobalizationProvider still must not mount until the locale is final: ui-react seeds both its
  // payload and its dictionary from props via useState, so a placeholder would stick. The loader
  // carries its own ThemeProvider because index.js mounts MUI's inside this one, and AirLoader
  // reads its sizes from the MUI theme.
  if (state.loading) {
    const brandTheme = getBrand().id === BRAND.CELEBRITY ? CelebrityTheme : Theme;
    return (
      <ThemeProvider theme={brandTheme}>
        <AirLoader open />
      </ThemeProvider>
    );
  }

  if (state.error) {
    // Enough for useGlobal() not to throw while App.js renders its error state.
    const fallback = {
      market: cookieCountry(),
      code: cookieCountry().toUpperCase(),
      language: cookieLanguage(),
    };
    return (
      <GlobalizationProvider
        defaultGlobalPayload={fallback}
        _isLoading={false}
        _isError={state.error}>
        {children}
      </GlobalizationProvider>
    );
  }

  return (
    <GlobalizationProvider
      defaultGlobalPayload={state.payload}
      dict={state.dictionary}
      _isLoading={false}>
      {children}
    </GlobalizationProvider>
  );
};

export default AirGlobalizationProvider;
